import { utils as ExcelUtils, write as writeWorkbook, read as readWorkbook, WorkBook } from 'xlsx';
import {
    FlatLanguageTranslations,
    FlatTranslations,
    LanguageStats,
    TranslationEditorState,
    TranslationTree
} from '../models/translation-editor.model';

const DEFAULT_SOURCE_LANGUAGE = 'en-us';

export const compositeKey = (language: string, key: string): string => `${language}::${key}`;
import { LANGUAGE_NAME_FALLBACKS } from './language-name-fallbacks';

const SHEET_NAME = 'Translations';
const KEY_COLUMN = 'key';

const displayNamesCache = new Map<string, Intl.DisplayNames>();
const getDisplayNames = (locale: string): Intl.DisplayNames => {
    let dn = displayNamesCache.get(locale);
    if (!dn) {
        dn = new Intl.DisplayNames([locale, 'en'], { type: 'language' });
        displayNamesCache.set(locale, dn);
    }
    return dn;
};

export class TranslationEditorOperations {
    /**
     * Export translations as an `.xlsx` file with one column per language:
     *
     * | key | en | de | tr | ... |
     */
    public static export(translations: FlatLanguageTranslations[]): void {
        const languages = translations.map((entry) => entry.language);
        const keys = new Set<string>();
        for (const entry of translations) {
            for (const key of Object.keys(entry.data)) {
                keys.add(key);
            }
        }
        const sortedKeys = [...keys].sort();

        const rows = sortedKeys.map((key) => {
            const row: Record<string, string> = { [KEY_COLUMN]: key };
            for (const entry of translations) {
                row[entry.language] = entry.data[key] ?? '';
            }
            return row;
        });

        const worksheet = ExcelUtils.json_to_sheet(rows, { header: [KEY_COLUMN, ...languages] });

        const headers = [KEY_COLUMN, ...languages];
        worksheet['!cols'] = headers.map((header) => ({
            wch: header === KEY_COLUMN ? 60 : 40
        }));
        for (let col = 0; col < headers.length; col++) {
            const ref = ExcelUtils.encode_cell({ r: 0, c: col });
            const cell = worksheet[ref];
            if (cell) {
                cell.s = { font: { bold: true } };
            }
        }

        const workbook: WorkBook = ExcelUtils.book_new();
        ExcelUtils.book_append_sheet(workbook, worksheet, SHEET_NAME);

        const buffer = writeWorkbook(workbook, { type: 'array', bookType: 'xlsx' }) as ArrayBuffer;
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `MDLA_translations_${new Date().toISOString().slice(0, 10)}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    }

    /**
     * Parse an `.xlsx` file previously produced by {@link export} back into a list of
     * flat translation maps, one per language column. The first column is treated as the
     * translation key; all remaining columns are treated as languages.
     */
    public static async import(file: File | Blob): Promise<FlatLanguageTranslations[]> {
        const buffer = await file.arrayBuffer();
        const workbook = readWorkbook(buffer, { type: 'array' });
        const sheetName = workbook.SheetNames.includes(SHEET_NAME) ? SHEET_NAME : workbook.SheetNames[0];
        if (!sheetName) {
            return [];
        }
        const worksheet = workbook.Sheets[sheetName];
        const rows = ExcelUtils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
        if (rows.length === 0) {
            return [];
        }

        const headers = Object.keys(rows[0]);
        const keyHeader = headers.find((h) => h.toLowerCase() === KEY_COLUMN) ?? headers[0];
        const languages = headers.filter((h) => h !== keyHeader);

        const result: FlatLanguageTranslations[] = languages.map((language) => ({ language, data: {} }));
        for (const row of rows) {
            const key = String(row[keyHeader] ?? '').trim();
            if (!key) {
                continue;
            }
            for (const entry of result) {
                const value = row[entry.language];
                entry.data[key] = value == null ? '' : String(value);
            }
        }
        return result;
    }

    /**
     * Converts a nested translation tree structure into a flat object with dot-notation keys.
     * Recursively traverses the tree, combining keys with dots to create fully qualified paths.
     * Null or undefined values are converted to empty strings.
     *
     * @param tree - The nested translation tree to flatten
     * @param prefix - Optional prefix to prepend to all generated keys (used in recursion)
     * @returns A flat object where keys are dot-notation paths and values are translation strings
     *
     * @example
     * const tree = { en: { hello: 'Hello', greeting: { welcome: 'Welcome' } } };
     * const flat = TranslationEditorOperations.flatten(tree, 'en');
     * // Result: { 'en.hello': 'Hello', 'en.greeting.welcome': 'Welcome' }
     */
    public static flatten(tree: TranslationTree, prefix = ''): FlatTranslations {
        const out: FlatTranslations = {};
        for (const [key, value] of Object.entries(tree)) {
            const path = prefix ? `${prefix}.${key}` : key;
            if (value !== null && typeof value === 'object') {
                Object.assign(out, this.flatten(value as TranslationTree, path));
            } else {
                out[path] = value == null ? '' : String(value);
            }
        }
        return out;
    }

    /**
     * Converts a flat translation object with dot-notation keys back into a nested tree structure.
     * Creates intermediate objects as needed to reconstruct the original hierarchy.
     * Keys are processed in sorted order to ensure consistent nesting.
     *
     * @param flat - The flat translation object with dot-notation keys
     * @returns A nested translation tree structure
     *
     * @example
     * const flat = { 'en.hello': 'Hello', 'en.greeting.welcome': 'Welcome' };
     * const tree = TranslationEditorOperations.unflatten(flat);
     * // Result: { en: { hello: 'Hello', greeting: { welcome: 'Welcome' } } }
     */
    public static unflatten(flat: FlatTranslations): TranslationTree {
        const root: TranslationTree = {};
        const sortedKeys = Object.keys(flat).sort();
        for (const fullKey of sortedKeys) {
            const parts = fullKey.split('.');
            let cursor: TranslationTree = root;
            for (let i = 0; i < parts.length - 1; i++) {
                const segment = parts[i];
                const next = cursor[segment];
                if (next == null || typeof next !== 'object') {
                    const fresh: TranslationTree = {};
                    cursor[segment] = fresh;
                    cursor = fresh;
                } else {
                    cursor = next;
                }
            }
            cursor[parts[parts.length - 1]] = flat[fullKey];
        }
        return root;
    }

    /**
     * Recursively sorts all keys in a translation tree in alphabetical order.
     * Preserves the nested structure while ensuring keys at each level are sorted.
     *
     * @param tree - The translation tree to sort
     * @returns A new translation tree with all keys sorted alphabetically at each level
     *
     * @example
     * const tree = { z: 'zulu', a: 'alpha', m: { z: 'zulu', a: 'alpha' } };
     * const sorted = TranslationEditorOperations.sortTree(tree);
     * // Result: { a: 'alpha', m: { a: 'alpha', z: 'zulu' }, z: 'zulu' }
     */
    public static sortTree(tree: TranslationTree): TranslationTree {
        const sorted: TranslationTree = {};
        for (const key of Object.keys(tree).sort()) {
            const value = tree[key];
            sorted[key] = value !== null && typeof value === 'object' ? this.sortTree(value as TranslationTree) : value;
        }
        return sorted;
    }

    /**
     * Formats a language code (e.g. `de-at`, `sv`) into a human-readable label such as
     * `German (Austria) (de-at)` or `Swedish (sv)`. Uses `Intl.DisplayNames` for the given
     * `locale` first, then falls back to a static English name table, and finally to the
     * raw code.
     */
    public static formatLanguageLabel(code: string, locale = 'en'): string {
        const parts = code.split('-');
        const languageSubtag = parts[0].toLowerCase();
        const regionSubtag = parts[1]?.toUpperCase();
        const bcp47 = regionSubtag ? `${languageSubtag}-${regionSubtag}` : languageSubtag;
        const name = getDisplayNames(locale).of(bcp47);
        if (name && name.toLowerCase() !== bcp47.toLowerCase()) {
            return `${name} (${code})`;
        }
        const fallback = LANGUAGE_NAME_FALLBACKS[bcp47.toLowerCase()];
        return fallback ? `${fallback} (${code})` : code;
    }

    public static formatLanguageLabelForDisplay(code: string, locale = 'en'): string {
        const fullLabel = this.formatLanguageLabel(code, locale);
        return fullLabel.replace(/\s*\([^)]*\)\s*$/, '');
    }

    public static withSourceLanguageFirst(languages: string[], source = DEFAULT_SOURCE_LANGUAGE): string[] {
        const rest = languages.filter((lang) => lang !== source);
        return [source, ...rest];
    }

    public static flatForLanguage(language: string, keys: string[], values: Record<string, string>): FlatTranslations {
        const flat: FlatTranslations = {};
        for (const key of keys) {
            flat[key] = values[compositeKey(language, key)] ?? '';
        }
        return flat;
    }

    /**
     * Build the flat set of *changed* keys for a language – only the keys whose current
     * value differs from the value originally loaded. This is the per-key diff that gets
     * merged onto the freshly re-fetched remote file at save time, so untouched keys are
     * never part of the payload and cannot clobber concurrent edits in unrelated areas.
     */
    public static changedFlatForLanguage(
        language: string,
        keys: string[],
        values: Record<string, string>,
        originals: Record<string, FlatTranslations>
    ): FlatTranslations {
        const flat: FlatTranslations = {};
        for (const key of keys) {
            const current = values[compositeKey(language, key)] ?? '';
            if (current !== (originals[language]?.[key] ?? '')) {
                flat[key] = current;
            }
        }
        return flat;
    }

    /**
     * Overlay a flat set of changed keys onto an existing translation tree, returning a new,
     * sorted tree. The remote tree is flattened, the local changes are layered on top per-key
     * (the caller's edits win), and the result is unflattened. Keys present in the remote tree
     * but untouched locally are preserved – this is what protects concurrent edits made by
     * other people while the editor was open.
     */
    public static applyChanges(remoteTree: TranslationTree, changes: FlatTranslations): TranslationTree {
        return this.unflatten({ ...this.flatten(remoteTree), ...changes });
    }

    public static findDirtyLanguages(
        languages: string[],
        keys: string[],
        values: Record<string, string>,
        originals: Record<string, FlatTranslations>
    ): string[] {
        return languages.filter((lang) =>
            keys.some((key) => (values[compositeKey(lang, key)] ?? '') !== (originals[lang]?.[key] ?? ''))
        );
    }

    public static buildStats(
        languages: string[],
        keys: string[],
        values: Record<string, string>,
        originals: Record<string, FlatTranslations>
    ): LanguageStats[] {
        const total = keys.length;
        return languages.map((code) => {
            let translated = 0;
            let dirty = 0;
            for (const key of keys) {
                const current = values[compositeKey(code, key)] ?? '';
                if (current.trim().length > 0) {
                    translated++;
                }
                if (current !== (originals[code]?.[key] ?? '')) {
                    dirty++;
                }
            }
            return {
                code,
                total,
                translated,
                missing: total - translated,
                dirty,
                percent: total === 0 ? 0 : Math.round((translated / total) * 100)
            };
        });
    }

    public static fromTrees(
        languages: string[],
        trees: Record<string, TranslationTree>,
        canonical?: FlatTranslations,
        source = DEFAULT_SOURCE_LANGUAGE
    ): Pick<TranslationEditorState, 'keys' | 'originals' | 'values'> {
        const canonicalFlat = canonical ?? {};
        const canonicalKeys = Object.keys(canonicalFlat);
        const useCanonical = canonicalKeys.length > 0;
        const originals: Record<string, FlatTranslations> = {};
        const keySet = new Set<string>(useCanonical ? canonicalKeys : undefined);
        for (const lang of languages) {
            const flat = this.flatten(trees[lang] ?? {});
            if (useCanonical) {
                const filtered: FlatTranslations = {};
                for (const key of canonicalKeys) {
                    filtered[key] = flat[key] ?? '';
                }
                originals[lang] = filtered;
            } else {
                originals[lang] = flat;
                for (const key of Object.keys(flat)) {
                    keySet.add(key);
                }
            }
        }
        const keys = [...keySet].sort();
        const values: Record<string, string> = {};
        for (const lang of languages) {
            for (const key of keys) {
                const original = originals[lang][key] ?? '';
                // Seed the source language from `keys.json` wherever its own file is missing or
                // blank for a key. The baseline (`originals`) is kept blank on purpose so the
                // seeded value is detected as a dirty edit and gets persisted on the next save.
                if (lang === source && !original.trim() && (canonicalFlat[key] ?? '').trim()) {
                    values[compositeKey(lang, key)] = canonicalFlat[key];
                    continue;
                }
                values[compositeKey(lang, key)] = original;
            }
        }
        return { keys, originals, values };
    }

    public static mergeImported(
        state: TranslationEditorState,
        imported: FlatLanguageTranslations[],
        source = DEFAULT_SOURCE_LANGUAGE
    ): TranslationEditorState {
        const importedLanguages = new Set(imported.map((entry) => entry.language));
        // Only keys that already exist (i.e. originate from `keys.json`) may be imported;
        // any unknown keys present in the file are silently ignored.
        const allowedKeys = new Set(state.keys);
        const keys = [...state.keys].sort();
        const languages = this.withSourceLanguageFirst(
            [...new Set([...state.languages, ...importedLanguages])],
            source
        );

        // Imported values are *staged* edits: the server baseline (`originals`) is kept
        // untouched so the import is detected as dirty and can actually be saved. New
        // languages start from an empty baseline so every imported value counts as a change.
        const originals: Record<string, FlatTranslations> = { ...state.originals };
        for (const lang of importedLanguages) {
            if (!originals[lang]) {
                originals[lang] = {};
            }
        }

        const importedByLanguage = new Map<string, FlatTranslations>();
        for (const entry of imported) {
            const filtered: FlatTranslations = {};
            for (const [key, value] of Object.entries(entry.data)) {
                if (allowedKeys.has(key)) {
                    filtered[key] = value;
                }
            }
            importedByLanguage.set(entry.language, filtered);
        }

        const values: Record<string, string> = {};
        for (const lang of languages) {
            const importedData = importedByLanguage.get(lang);
            for (const key of keys) {
                const composite = compositeKey(lang, key);
                values[composite] =
                    importedData && key in importedData
                        ? importedData[key]
                        : (state.values[composite] ?? originals[lang]?.[key] ?? '');
            }
        }
        return { languages, keys, originals, values };
    }
}
